import { memo, useCallback, useEffect, useState } from "react";

// Helpers
import { useTranslate, classNames } from "helpers";

// UI
import { Button } from "ui/components/Button/Button";
import MultiDropdown from "ui/components/Dropdown/MultiDropdown/MultiDropdown";
import { MultiDropdownOption } from "ui/components/Dropdown/MultiDropdown/types";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";

import { VehicleCategoryEditCardProps } from "./types";
import { updateFleetSettingsAPI } from "services/api/settings/fleet/api";
import { VEHICLE_WIZARD_STEP_LABELS } from "services/api/settings/fleet/consts";

const VehicleCategoryEditCard: React.FC<VehicleCategoryEditCardProps> = (props) => {
    const { show, categoryOptions, selectedCategoryIds, vehicleSteps, enabledVehicleStepKeys, onSuccess, onClose } = props;

    const { t } = useTranslate();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [editCategoryIds, setEditCategoryIds] = useState<string[]>(selectedCategoryIds);
    const [editStepKeys, setEditStepKeys] = useState<string[]>(enabledVehicleStepKeys);
    const [categoryError, setCategoryError] = useState<string>("");

    useEffect(() => {
        if (show) {
            setEditCategoryIds(selectedCategoryIds);
            setEditStepKeys(enabledVehicleStepKeys);
            setCategoryError("");
        }
    }, [show, selectedCategoryIds, enabledVehicleStepKeys]);

    const handleStepToggle = useCallback((key: string, checked: boolean) => {
        setEditStepKeys((prev) =>
            checked ? [...new Set([...prev, key])] : prev.filter((k) => k !== key)
        );
    }, []);

    const handleCategoryChange = useCallback(
        (name: string, items: MultiDropdownOption[]) => {
            const ids = items.map((item) => item.value);
            if (!ids.includes("A")) {
                setCategoryError(t("admin.settings.fleet.vehiclecategories.edit.required"));
                ids.unshift("A");
            } else {
                setCategoryError("");
            }
            setEditCategoryIds(ids);
        },
        [t]
    );

    const handleFormSubmit = async (e: any) => {
        e.preventDefault();

        if (editCategoryIds.length === 0) {
            return;
        }

        setIsLoading(true);

        try {
            await updateFleetSettingsAPI(
                { vehicleCategoryIds: editCategoryIds, vehicleStepKeys: editStepKeys },
                t
            );
            if (onSuccess) onSuccess();
        } catch {
            setIsLoading(false);
            return;
        }

        setIsLoading(false);
    };

    return (
        <Modal show={show} onHide={onClose}>
            <ModalHeader
                title={t("admin.settings.fleet.vehiclecategories.edit.title")}
                icon="/media/images/add-icon.svg"
                onHide={onClose}
            />
            <ModalBody>
                <div className="flex gap-4 ">
                    <div className="w-full m-auto h-screen">
                        <div className="flex gap-3 h-full">
                            <form noValidate className="text-left flex-1" onSubmit={handleFormSubmit}>
                                <div className="p-4">
                                    <div className="mb-3">
                                        <MultiDropdown
                                            name="vehicleCategoryIds"
                                            label={t("admin.settings.fleet.vehiclecategories.label")}
                                            placeholder={t("admin.settings.fleet.vehiclecategories.placeholder")}
                                            options={categoryOptions}
                                            value={editCategoryIds}
                                            onChange={handleCategoryChange}
                                            width="full"
                                        />
                                        {categoryError && (
                                            <p className="mt-1 text-sm text-red-600">{categoryError}</p>
                                        )}
                                    </div>

                                    <div className="mb-3 border-t border-slate-200 pt-4">
                                        <p className="text-sm font-semibold text-slate-700 mb-1">
                                            Koraci unosa vozila
                                        </p>
                                        <p className="text-xs text-slate-500 mb-3">
                                            Obavezni koraci se ne mogu isključiti.
                                        </p>
                                        <div className="flex flex-col gap-2">
                                            {vehicleSteps.map((step) => {
                                                const selected = step.required || editStepKeys.includes(step.key);
                                                const handleClick = () => {
                                                    if (step.required) return;
                                                    handleStepToggle(step.key, !editStepKeys.includes(step.key));
                                                };
                                                return (
                                                    <button
                                                        key={step.key}
                                                        type="button"
                                                        onClick={handleClick}
                                                        disabled={step.required}
                                                        className={classNames(
                                                            "flex w-full items-center gap-2 px-3 py-2.5 rounded-sm border text-sm transition-colors text-left",
                                                            selected
                                                                ? "bg-sky-50 border-sky-500 text-sky-700"
                                                                : "bg-white border-slate-200 text-slate-600 hover:border-slate-300",
                                                            step.required ? "opacity-70 cursor-default" : "cursor-pointer"
                                                        )}
                                                    >
                                                        <span
                                                            className={classNames(
                                                                "w-4 h-4 rounded border flex items-center justify-center flex-shrink-0",
                                                                selected
                                                                    ? "bg-sky-600 border-sky-600 text-white"
                                                                    : "border-slate-300 bg-white"
                                                            )}
                                                        >
                                                            {selected && (
                                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                                </svg>
                                                            )}
                                                        </span>
                                                        {VEHICLE_WIZARD_STEP_LABELS[step.key] ?? step.key}
                                                        {step.required ? " (obavezno)" : ""}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                                <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                                    <Button
                                        variant="outlined"
                                        children={t("admin.close")}
                                        onClick={() => onClose()}
                                    />
                                    <Button
                                        variant="primary"
                                        children={t("admin.save")}
                                        type="submit"
                                    />
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
};
export default memo(VehicleCategoryEditCard);
